import {Component, Input, OnInit} from '@angular/core';
import {ProductPage} from '../../classes/product.page';
import {Logger} from '../../logging/default-log.service';
import {ProductService} from '../../service/product.service';
import {UserService} from '../../service/user.service';
import {Router} from '@angular/router';
import {FilterService} from '../../service/filter.service';
import {ShopHelper} from '../../helper/shop.helper';
import {FilterState} from '../../classes/filter.state';
import {ApplicationService} from '../../service/application.service';
import {PlantscapingService} from '../../service/plantscaping.service';
import {LazyLoadDirective} from '../lazyload/ng-lazy-load.directive';
import {ProductItemGridComponent} from './productitem.grid.component';
import {NgClass} from '@angular/common';

@Component({
	selector: 'spotlight-products',
	templateUrl: './spotlight.products.component.html',
	standalone: true,
	imports: [NgClass, ProductItemGridComponent, LazyLoadDirective]
})
export class SpotlightProductsComponent implements OnInit {
	productPage: ProductPage;
	loading: boolean = true;
	@Input() description: string;
	@Input() filterUrl: string;
	@Input() origin: string;
	@Input() showAll: string;
	@Input() title: string;

	private windowObj = <any>window;

	constructor(
		private productService: ProductService,
		private filterService: FilterService,
		private applicationService: ApplicationService,
		private plantscapingService: PlantscapingService,
		private router: Router,
		userService: UserService,
		private logger: Logger
	) {
		this._userService = userService;
	}

	private _userService: UserService;

	get userService(): UserService {
		return this._userService;
	}

	ngOnInit() {
		this.filterUrl = this.setFilterDefaultsIfNeeded(this.filterUrl);

		const filterState = new FilterState([]);

		this.filterService.initFilterStateForRequestParams(
			filterState,
			this.router.parseUrl(this.filterUrl).queryParams
		);

		this.filterService.initSelectedLanguage(filterState);

		// Spotlight products are not accessed through plantscaping, resetting this session variable.
		this.plantscapingService.setPlantscapingInSession(0);

		// Remove everything before the querystring '?', including the question mark itself,
		// as it is added by the productService logic.
		this.loadProducts(filterState);
	}

	viewProductDetail(detailUrl: string) {
		detailUrl += `;origin=${this.origin}`;

		ShopHelper.goToWebshopUrl(detailUrl);
	}

	loadProducts(filterState: FilterState) {
		this.productService
			.getProducts(filterState.getParams())
			.then((productPage) => {
				this.productPage = productPage;
				this.loading = false;

				setTimeout(() => {
					const anchorHash = this.windowObj.location.hash;
					this.productService.triggerScrollToProductOnWebsite(anchorHash);
				}, 700);
			})
			.catch((error) => {
				this.loading = false;
				this.logger.error('Error loading the products from the server', error);
			});
	}

	// code is of type 'string' instead of 'boolean'. So we check for a string value 'true' in this case.
	hasShowAllButton(): boolean {
		return this.showAll === 'true' && this.productPage !== undefined;
	}

	// Somehow the value that is input through the wrappers for the spotlight products item type and smart object

	isWebsite(): boolean {
		return this.applicationService.isBrowsingWebsite(window.location.href);
	}

	getShowAllUrl(): string | null {
		if (this.showAll) {
			return this.getShowAllProductsUrl(this.filterUrl);
		}

		return null;
	}

	private setFilterDefaultsIfNeeded(filterUrl: string): string {
		// If no mode is defined in the smart object url, use the default 'all' mode.
		let filterQueryString = this.addQueryParamIfNotExists(
			filterUrl,
			'mode',
			'all'
		);

		// If no sorting is defined in the smart object url, use the default 'random' sorting.
		filterQueryString = this.addQueryParamIfNotExists(
			filterQueryString,
			'sorting',
			'random'
		);

		return filterQueryString;
	}

	private getShowAllProductsUrl(queryString: string) {
		// Remove query params, which should not be kept when navigating to the 'all products' overview.
		queryString = this.removeURLParameter(queryString, 'sorting');
		queryString = this.removeURLParameter(queryString, 'pageSize');

		return ShopHelper.getWebshopUrl(this.userService.getCurrentSiteLanguage() +
			'/products?' +
			queryString.split('?')[1]);
	}

	private removeURLParameter(url: string, parameter: string) {
		const urlParts = url.split('?');

		if (urlParts.length >= 2) {
			const prefix = encodeURIComponent(parameter) + '=';
			const pars = urlParts[1].split(/[&;]/g);

			// reverse iteration as may be destructive
			for (let i = pars.length - 1; i >= 0; i -= 1) {
				// idiom for string.startsWith
				if (pars[i].lastIndexOf(prefix, 0) !== -1) {
					pars.splice(i, 1);
				}
			}

			return urlParts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
		}

		return url;
	}

	private addQueryParamIfNotExists(queryString: string, paramName: string, paramValue: string) {
		if (queryString && queryString.indexOf(paramName) === -1) {
			queryString += queryString.indexOf('?') === -1 ? '?' : '&';
			queryString += `${paramName}=${paramValue}`;
		}

		return queryString;
	}
}
